import THREE from "three";

function translateParams(params) {
  if (!params) return params;
  const out = { ...params };
  if ('specularColor' in out) {out.specular = out.specularColor;delete out.specularColor;}
  if ('specularPower' in out) {out.shininess = out.specularPower;delete out.specularPower;}
  if ('fresnelStrength' in out) {out.reflectivity = out.fresnelStrength;delete out.fresnelStrength;}
  if ('vertexColors' in out) {out.vertexColors = out.vertexColors ? THREE.VertexColors : THREE.NoColors;}
  return out;
}

/**
 * Lit surface material for opaque and transparent 3D geometry.
 *
 * Uses an energy-conserving Blinn-Phong + Schlick-Fresnel BRDF for direct lights, a Lagarde-style
 * PMREM for image-based reflection, and Ashikhmin-Shirley diffuse attenuation to couple diffuse
 * and specular energy at grazing angles.
 *
 * @alias Autodesk.Viewing.Scene.StandardMaterial
 * @memberOf Autodesk.Viewing.Scene
 * @class
 */
export class StandardMaterial extends THREE.MeshPhongMaterial {
  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial
   * @param {object}                          [params]
   * @param {Autodesk.Viewing.Scene.Color}    [params.color]           Diffuse albedo. Default: white.
   * @param {number}                          [params.opacity]         Surface opacity in `[0, 1]`. Default: `1`.
   * @param {Autodesk.Viewing.Scene.Color}    [params.specularColor]   Specular tint / F0 (see class description). Default: dark grey.
   * @param {number}                          [params.specularPower]   Blinn-Phong exponent and PMREM mip selector (see class description). Default: `30`.
   * @param {number}                          [params.fresnelStrength] Fresnel intensity scalar. `0.0` bypasses Fresnel entirely. Default: `1`.
   * @param {boolean}                         [params.metal]           Metal shading branch. Default: `false`.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.map]             Diffuse (albedo) texture.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.specularMap]     Specular texture.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.normalMap]       Tangent-space normal map. Mutually exclusive with `bumpMap`.
   * @param {Autodesk.Viewing.Math.Vector2}   [params.normalScale]     Normal-map intensity. Default: `(1, 1)`.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.bumpMap]         Height-based bump map. Mutually exclusive with `normalMap`.
   * @param {number}                          [params.bumpScale]       Bump-map intensity. Default: `1`.
   * @param {Autodesk.Viewing.Scene.Texture}  [params.alphaMap]        Alpha mask texture.
   * @param {boolean}                         [params.vertexColors]    Multiply surface color by per-vertex colors. Default: `false`.
   * @param {Autodesk.Viewing.Scene.Side}     [params.side]            Faces to render. Default: `Side.Front`.
   * @param {boolean}                         [params.depthWrite]      Write to the depth buffer. Default: `true`.
   * @param {boolean}                         [params.depthTest]       Depth-test against the depth buffer. Default: `true`.
   * @param {boolean}                         [params.transparent]     Render in the transparent pass with alpha blending. Default: `false`.
   */
  constructor(params) {
    super(translateParams(params));

    // metal is not a standard THREE.MeshPhongMaterial constructor parameter, so we have to handle it ourselves.
    if (params?.metal !== undefined) this.metal = params.metal;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getColor
   * @returns {Autodesk.Viewing.Scene.Color} A defensive clone of the diffuse albedo color.
   */
  getColor() {return this.color.clone();}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setColor
   * @param {Autodesk.Viewing.Scene.Color} c Diffuse albedo. Copied into the existing Color.
   */
  setColor(c) {
    this.color.copy(c);
    this.uniformsNeedUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getOpacity
   * @returns {number} Surface opacity in `[0, 1]`.
   */
  getOpacity() {return this.opacity;}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setOpacity
   * @param {number} o Surface opacity in `[0, 1]`. Requires `setTransparent(true)` for `< 1`.
   */
  setOpacity(o) {
    this.opacity = o;
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the specular color.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getSpecularColor
   * @returns {Autodesk.Viewing.Scene.Color} A defensive clone of the specular color.
   */
  getSpecularColor() {return this.specular.clone();}

  /**
   * Sets the specular color.
   * Drives the direct-light specular tint, the Schlick F0 in the IBL term, and the reflection tint when `metal = true`.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setSpecularColor
   * @param {Autodesk.Viewing.Scene.Color} c Specular tint / F0 color. Copied into the existing Color.
   */
  setSpecularColor(c) {
    this.specular.copy(c);
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the Blinn-Phong specular exponent.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getSpecularPower
   * @returns {number}
   */
  getSpecularPower() {return this.shininess;}

  /**
   * Sets the Blinn-Phong specular exponent.
   * Drives both the direct-light highlight sharpness and the PMREM mip selector for IBL reflections — a single value
   * controls both. Not in `[0, 1]`; typical range `[1, 2048]`. Not equivalent to a roughness or glossiness factor.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setSpecularPower
   * @param {number} s Blinn-Phong exponent. Typical range `[1, 2048]`.
   */
  setSpecularPower(s) {
    this.shininess = s;
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the Fresnel intensity scalar.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getFresnelStrength
   * @returns {number}
   */
  getFresnelStrength() {return this.reflectivity;}

  /**
   * Sets the Fresnel intensity scalar.
   * Scales the alpha-Fresnel applied to transparent materials and the clearcoat Fresnel strength.
   * Setting exactly `0.0` bypasses Fresnel entirely. **Not a BRDF F0** — F0 is `specularColor`.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setFresnelStrength
   * @param {number} r Fresnel intensity. `0.0` disables Fresnel.
   */
  setFresnelStrength(r) {
    this.reflectivity = r;
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the normal-map intensity scale.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getNormalScale
   * @returns {Autodesk.Viewing.Math.Vector2} A defensive clone of the normal-map intensity scale.
   */
  getNormalScale() {return this.normalScale.clone();}

  /**
   * Sets the normal-map intensity scale.
   * Note: `normalScale.x` damps Fresnel as a side-effect — high values implicitly
   * read as "rougher" surfaces, reducing reflection sharpness.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setNormalScale
   * @param {Autodesk.Viewing.Math.Vector2} v Normal-map intensity. Copied into the existing Vector2.
   */
  setNormalScale(v) {
    this.normalScale.copy(v);
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the bump-map intensity scale.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getBumpScale
   * @returns {number} Bump-map intensity scale.
   */
  getBumpScale() {return this.bumpScale;}

  /**
   * Sets the bump-map intensity scale.
   * Note: high values damp Fresnel as a side-effect — bumpy surfaces implicitly read as rougher, reducing reflection sharpness.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setBumpScale
   * @param {number} s Bump-map intensity.
   */
  setBumpScale(s) {
    this.bumpScale = s;
    this.uniformsNeedUpdate = true;
  }

  /**
   * Returns the diffuse (albedo) texture.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Diffuse (albedo) texture, or `null`.
   */
  getMap() {return this.map;}

  /**
   * Sets the diffuse (albedo) texture.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Diffuse (albedo) texture, or `null`.
   */
  setMap(t) {
    this.map = t;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getSpecularMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Specular texture, or `null`.
   */
  getSpecularMap() {return this.specularMap;}

  /**
   * Sets the specular texture.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setSpecularMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Specular texture, or `null`.
   */
  setSpecularMap(t) {
    this.specularMap = t;
    this.needsUpdate = true;
  }

  /**
   * Note: `bumpMap` and `normalMap` share a single texture slot in WebGPU —
   * only one can be active at a time.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getNormalMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Tangent-space normal map, or `null`.
   */
  getNormalMap() {return this.normalMap;}

  /**
   * Sets the tangent-space normal map.
   * Note: setting both `normalMap` and `bumpMap` are mutually exclusive.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setNormalMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Normal map, or `null`.
   */
  setNormalMap(t) {
    this.normalMap = t;
    this.needsUpdate = true;
  }

  /**
   * Gets the height-based bump map.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getBumpMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Height-based bump map, or `null`.
   */
  getBumpMap() {return this.bumpMap;}

  /**
   * Sets the height-based bump map.
   * Note: setting both `bumpMap` and `normalMap` are mutually exclusive.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setBumpMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Bump map, or `null`.
   */
  setBumpMap(t) {
    this.bumpMap = t;
    this.needsUpdate = true;
  }

  /**
   * Gets the alpha mask texture.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getAlphaMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Alpha mask texture, or `null`.
   */
  getAlphaMap() {return this.alphaMap;}

  /**
   * Sets the alpha mask texture.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setAlphaMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Alpha mask texture, or `null`.
   */
  setAlphaMap(t) {
    this.alphaMap = t;
    this.needsUpdate = true;
  }

  /**
   * Whether the metal shading branch is active. When `true`, the shader skips
   * Fresnel in the IBL term and tints the reflection by `specularColor`.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getMetal
   * @returns {boolean}
   */
  getMetal() {return this.metal;}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setMetal
   * @param {boolean} v Metal shading flag. Avoid toggling at runtime in WebGPU.
   */
  setMetal(v) {
    this.metal = v;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getVertexColors
   * @returns {boolean} Whether to multiply the surface color by per-vertex colors.
   */
  getVertexColors() {return this.vertexColors !== THREE.NoColors;}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setVertexColors
   * @param {boolean} v Whether to multiply the surface color by per-vertex colors.
   */
  setVertexColors(v) {
    this.vertexColors = v ? THREE.VertexColors : THREE.NoColors;
    this.needsUpdate = true;
  }

  /**
   * Gets which face(s) of a polygon are rendered.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getSide
   * @returns {Autodesk.Viewing.Scene.Side} Faces to render.
   */
  getSide() {return this.side;}

  /**
   * Sets which face(s) of a polygon are rendered.
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setSide
   * @param {Autodesk.Viewing.Scene.Side} s Faces to render.
   */
  setSide(s) {
    this.side = s;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getDepthWrite
   * @returns {boolean} Whether to write to the depth buffer.
   */
  getDepthWrite() {return this.depthWrite;}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setDepthWrite
   * @param {boolean} v Whether to write to the depth buffer.
   */
  setDepthWrite(v) {
    this.depthWrite = v;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getDepthTest
   * @returns {boolean} Whether to depth-test against the depth buffer.
   */
  getDepthTest() {return this.depthTest;}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setDepthTest
   * @param {boolean} v Whether to depth-test against the depth buffer.
   */
  setDepthTest(v) {
    this.depthTest = v;
    this.needsUpdate = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#getTransparent
   * @returns {boolean} Whether the material renders in the transparent pass.
   */
  getTransparent() {return this.transparent;}

  /**
   * @alias Autodesk.Viewing.Scene.StandardMaterial#setTransparent
   * @param {boolean} v Whether the material renders in the transparent pass with alpha blending.
   */
  setTransparent(v) {
    this.transparent = v;
    this.needsUpdate = true;
  }
}